import type {
  ConditionFieldDescription,
  ConditionFunctionConfig,
} from "@smartsuite/automation-engine-api";
import { RECORD_CREATED_TRIGGER_IDS } from "../../../../../../../utils/constants";
import type { FnConfig } from "./types";
import { getSelectedFunction } from "./selected-function";

const TRIGGERS_WITHOUT_CHANGES_FN = RECORD_CREATED_TRIGGER_IDS.filter(
  (triggerId) => triggerId !== "updated-record",
);

/**
 * The operator a condition starts on.
 *
 * Which operators a field offers belongs to the integration that owns it; which
 * of them a new condition starts on is the editor's, the same way the labels
 * and value controls are. The two cannot be read off one ordered list: the
 * editor shows "Any" first almost everywhere and has never started on it, while
 * a select starts on "Changes", which it shows second.
 *
 * So the preference below picks, and the description constrains — a preference
 * the field does not offer gives way to the first operator it does, and a new
 * condition can never be created holding one the engine would reject
 * (STR-14903).
 */
export const getDefaultFunction = (
  availableFunctions: FnConfig[],
  conditionFieldDescription?: ConditionFieldDescription,
  triggerId?: string,
  actionId?: string,
  excludeChangesFn?: boolean,
): ConditionFunctionConfig | undefined => {
  const preferred = preferredFunction(
    conditionFieldDescription,
    triggerId,
    actionId,
    excludeChangesFn,
  );
  const preferredName = preferred?.function.oneofKind;
  if (
    preferredName !== undefined &&
    availableFunctions.some(([name]) => name === preferredName)
  ) {
    return preferred;
  }
  const offered = availableFunctions[0];
  return offered === undefined ? preferred : getSelectedFunction(offered[0]);
};

const preferredFunction = (
  conditionFieldDescription?: ConditionFieldDescription,
  triggerId?: string,
  actionId?: string,
  excludeChangesFn?: boolean,
): ConditionFunctionConfig | undefined => {
  if (conditionFieldDescription === undefined) {
    return undefined;
  }
  const noChanges = actionId !== undefined || excludeChangesFn === true;
  switch (conditionFieldDescription.editor?.editor.oneofKind) {
    case "text":
      // temporary link by icon as more strict condition require more work
      if (conditionFieldDescription.icon === "clock") {
        // because contains is not supported for time field
        return {
          function: {
            oneofKind: "isEquals",
            isEquals: {
              right: { values: [] },
            },
          },
        };
      }
      return {
        function: {
          oneofKind: "contains",
          contains: {
            right: { values: [] },
          },
        },
      };
    case "smartdoc":
      return {
        function: {
          oneofKind: "isNotEmpty",
          isNotEmpty: {},
        },
      };
    case "date":
      return {
        function: {
          oneofKind: "isEquals",
          isEquals: {
            right: { values: [] },
          },
        },
      };
    case "select":
    case "tag":
      if (noChanges || TRIGGERS_WITHOUT_CHANGES_FN.includes(triggerId ?? "")) {
        return {
          function: {
            oneofKind: "isEquals",
            isEquals: {
              right: { values: [] },
            },
          },
        };
      }
      return {
        function: {
          oneofKind: "changes",
          changes: {
            before: {
              function: {
                oneofKind: "isAny",
                isAny: {},
              },
            },
            after: {
              function: {
                oneofKind: "isAny",
                isAny: {},
              },
            },
          },
        },
      };
    case "member":
      if (noChanges || TRIGGERS_WITHOUT_CHANGES_FN.includes(triggerId ?? "")) {
        return {
          function: {
            oneofKind: "isEquals",
            isEquals: {
              right: { values: [] },
            },
          },
        };
      }
      return {
        function: {
          oneofKind: "changes",
          changes: {
            before: {
              function: {
                oneofKind: "isAny",
                isAny: {},
              },
            },
            after: {
              function: {
                oneofKind: "isAny",
                isAny: {},
              },
            },
          },
        },
      };
    case "email":
      return {
        function: {
          oneofKind: "contains",
          contains: {
            right: { values: [] },
          },
        },
      };
    case "link":
      return {
        function: {
          oneofKind: "contains",
          contains: {
            right: { values: [] },
          },
        },
      };
    case "phone":
      return {
        function: {
          oneofKind: "contains",
          contains: {
            right: { values: [] },
          },
        },
      };
    case "number":
      return {
        function: {
          oneofKind: "greaterThan",
          greaterThan: {
            right: { values: [] },
          },
        },
      };
    case "checklist":
      return {
        function: {
          oneofKind: "allItemsResolved",
          allItemsResolved: {},
        },
      };
    case "status":
      return {
        function: {
          oneofKind: "isEquals",
          isEquals: {
            right: { values: [] },
          },
        },
      };
    default:
      return undefined;
  }
};
